import { HttpClient } from '@angular/common/http';
import { VerificationRequestStatus } from '@app/core/enums/verification-requests.enum';
import { VERIFICATION_TYPE } from '@app/shared/const';
import { environment } from '@env/environment';
import { Observable, catchError, delay, map, mergeMap, timeout } from 'rxjs';
import { ApiClientConfig } from '../api-client.config';
import {
  BusinessVerificationDataResponse,
  FormVerificationRequestPolicy,
  GetOverviewBody,
  VerificationRequest,
  VerificationRequestBody,
  VerificationRequestBusinessOfUser,
  VerificationRequestBusinessOfUserResponse,
  VerificationRequestBusinessResponse,
  VerificationRequestPolicy,
  VerificationRequestPolicyResponse,
  VerificationRequestPolicyResponseMessage,
  VerificationRequestProfileOfUser,
  VerificationRequestProfileOfUserResponse,
  VerificationRequestProfileResponse
} from './api.verification-request.models';

export class VerificationRequestsApi {
  private apiUrl = environment.API_URL;
  VERIFICATION_REQUEST = '/verification/admin/verification';
  BUSINESS_VERIFICATION_REQUEST = '/verification/business';
  VERIFICATION_POLICY = '/verification/policy';
  VERIFICATION_PROFILE_CENSORSHIP = '/verification/admin/verification/profile/censorship';
  VERIFICATION_BUSINESS_CENSORSHIP = '/verification/admin/verification/business/censorship';
  constructor(
    public readonly http: HttpClient,
    public config: ApiClientConfig
  ) {}

  getVerificationRequestsProfile({
    status = '',
    keySearch = '',
    pageNum = 0,
    pageSize = 10,
    zip_code = [],
    isLoadAll = false
  }: VerificationRequestBody): Observable<VerificationRequest[]> {
    const queryParams = new URLSearchParams();
    queryParams.set('status', status);
    if (keySearch !== '') {
      queryParams.set('keySearch', keySearch);
    }
    queryParams.set('root', JSON.stringify(zip_code));
    queryParams.set('isLoadAll', isLoadAll.toString());
    queryParams.set('pageNum', pageNum.toString());
    queryParams.set('pageSize', pageSize.toString());

    const url = `${this.apiUrl}/verification/admin/verification/profile?${queryParams.toString()}`;

    return this.http.get<VerificationRequestProfileResponse>(url).pipe(
      timeout(this.config.responseTimeout),
      delay(2000),
      mergeMap(() =>
        this.http.get<VerificationRequestProfileResponse>(url).pipe(
          map(res => {
            return res.data;
          })
        )
      ),
      catchError(error => {
        throw error.error;
      })
    );
  }

  getVerificationRequestProfileByUserId(
    userId: string,
    status: string
  ): Observable<VerificationRequestProfileOfUser[]> {
    const url = `${this.apiUrl}${this.VERIFICATION_REQUEST}/profile/${userId}?status=${VerificationRequestStatus.NONE}`;
    return this.http.get<VerificationRequestProfileOfUserResponse>(url).pipe(
      timeout(this.config.responseTimeout),
      map(res => {
        return res.data;
      }),
      catchError(error => {
        throw error.error;
      })
    );
  }

  getVerificationRequestBusiness({
    status = '',
    keySearch = '',
    pageNum = 0,
    pageSize = 10,
    zip_code = [],
    isLoadAll = false
  }: VerificationRequestBody): Observable<VerificationRequest[]> {
    const queryParams = new URLSearchParams();
    if (keySearch !== '') {
      queryParams.set('keySearch', keySearch);
    }
    queryParams.set('root', JSON.stringify(zip_code));
    queryParams.set('isLoadAll', isLoadAll.toString());
    queryParams.set('pageNum', pageNum.toString());
    queryParams.set('pageSize', pageSize.toString());

    const url = `${this.apiUrl}${
      this.VERIFICATION_REQUEST
    }/business?status=${status}&object_type=BUSINESS_PAGE&${queryParams.toString()}`;
    return this.http.get<VerificationRequestBusinessResponse>(url).pipe(
      timeout(this.config.responseTimeout),
      delay(2000),
      mergeMap(() =>
        this.http.get<VerificationRequestBusinessResponse>(url).pipe(
          map(res => {
            return res.data;
          })
        )
      ),
      catchError(error => {
        throw error.error;
      })
    );
  }

  getBusinessAccountVerificationRequests(
    status: string,
    keySearch: string,
    isLoadAll: boolean,
    pageNum = 0,
    pageSize = 10,
    root = '',
    objectType = VERIFICATION_TYPE.businessAccount
  ): Observable<VerificationRequestProfileResponse> {
    const requestUrl = `${this.apiUrl}${
      this.VERIFICATION_REQUEST
    }/business?status=${status}&keySearch=${encodeURI(
      keySearch
    )}&object_type=${objectType}&pageSize=${pageSize}&pageNum=${pageNum}&root=${root}&isLoadAll=${isLoadAll}`;
    return this.http.get<VerificationRequestProfileResponse>(requestUrl).pipe(
      timeout(this.config.responseTimeout),
      delay(2000),
      mergeMap(() =>
        this.http.get<VerificationRequestProfileResponse>(requestUrl).pipe(
          timeout(this.config.responseTimeout),
          map(res => {
            return res;
          })
        )
      ),
      catchError(error => {
        throw error.error;
      })
    );
  }

  getBusinessAccountVerificationRequestById(
    userId: string,
    objectId: string
  ): Observable<BusinessVerificationDataResponse> {
    return this.http
      .get<BusinessVerificationDataResponse>(
        `${this.apiUrl}${this.VERIFICATION_REQUEST}/business/${userId}/${objectId}?status=${VerificationRequestStatus.NONE}`
      )
      .pipe(
        timeout(this.config.responseTimeout),
        map(res => {
          return res;
        }),
        catchError(error => {
          throw error.error;
        })
      );
  }

  getPolicy(typePolicy: string): Observable<VerificationRequestPolicy[]> {
    const url = `${this.apiUrl}${this.VERIFICATION_POLICY}/get-all?type=${typePolicy}`;
    return this.http.get<VerificationRequestPolicyResponse>(url).pipe(
      timeout(this.config.responseTimeout),
      map(res => {
        return res.data;
      }),
      catchError(error => {
        throw error.error;
      })
    );
  }

  getVerificationRequestBusinessByUserId(
    userId: string,
    pageId: string,
    status: string
  ): Observable<VerificationRequestBusinessOfUser[]> {
    const url = `${this.apiUrl}${this.VERIFICATION_REQUEST}/business/${userId}/${pageId}?status=${VerificationRequestStatus.NONE}`;
    return this.http.get<VerificationRequestBusinessOfUserResponse>(url).pipe(
      timeout(this.config.responseTimeout),
      map(res => {
        return res.data;
      }),
      catchError(error => {
        throw error.error;
      })
    );
  }

  putVerificationRequestPolicy(
    data: FormVerificationRequestPolicy,
    type: string
  ): Observable<string> {
    const requestType =
      type === 'PROFILE'
        ? this.VERIFICATION_PROFILE_CENSORSHIP
        : this.VERIFICATION_BUSINESS_CENSORSHIP;
    const url = `${this.apiUrl}${requestType}`;
    return this.http.put<VerificationRequestPolicyResponseMessage>(url, data).pipe(
      timeout(this.config.responseTimeout),
      map(res => {
        return res.data;
      }),
      catchError(error => {
        throw error.error;
      })
    );
  }

  putVerifyUserBadge(
    userId: string,
    brandsOnSoctrip: 'SOCTRIP_MASTER' | 'SOCTRIP_STAR'
  ): Observable<any> {
    const url = `${this.apiUrl}/user/admin/user/${userId}/brands?brandsOnSoctrip=${brandsOnSoctrip}&isUpdate=false`;
    return this.http.put(url, { secret_key: environment.SECRET_KEY }).pipe(
      timeout(this.config.responseTimeout),
      map(res => {
        return res;
      }),
      catchError(error => {
        throw error.error;
      })
    );
  }

  putVerifyFanpageBadge(
    pageId: string,
    brandsOnSoctrip: 'SOCTRIP_MASTER' | 'SOCTRIP_STAR'
  ): Observable<any> {
    const url = `${this.apiUrl}/page/management/page/${pageId}/brands?brandsOnSoctrip=${brandsOnSoctrip}&isUpdate=false`;
    return this.http.put(url, { secret_key: environment.SECRET_KEY }).pipe(
      timeout(this.config.responseTimeout),
      map(res => {
        return res;
      }),
      catchError(error => {
        throw error.error;
      })
    );
  }

  updateVerificationRequest(
    data: FormVerificationRequestPolicy,
    type: string
  ): Observable<boolean> {
    const requestType =
      type === 'PROFILE'
        ? this.VERIFICATION_PROFILE_CENSORSHIP
        : this.VERIFICATION_BUSINESS_CENSORSHIP;
    const url = `${this.apiUrl}${requestType}`;
    return this.http.put<VerificationRequestPolicyResponseMessage>(url, data).pipe(
      timeout(this.config.responseTimeout),
      map(res => {
        return res?.success;
      }),
      catchError(error => {
        throw error.error;
      })
    );
  }

  getFriend(pageNum?: number, userId?: string): Observable<number> {
    const url = `${this.apiUrl}/friend/friends?page=${pageNum}&specifiedUserId=${userId}`;
    return this.http.get(url).pipe(
      timeout(this.config.responseTimeout),
      map((res: any) => {
        return res.data.number_of_elements;
      }),
      catchError(error => {
        throw error.error;
      })
    );
  }

  getOverviewData(body: GetOverviewBody) {
    const url = `${this.apiUrl}${this.VERIFICATION_REQUEST}/overview/${body.object_id}?objectType=${body.objectType}`;
    return this.http.get<any>(url).pipe(
      timeout(this.config.responseTimeout),
      map(res => {
        return res.data;
      }),
      catchError(error => {
        throw error.error;
      })
    );
  }
}
